/** One backtest trade, verified: stored values vs recalculated indicators, rule checks, warnings, manual status. */
import { useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, Grid, MenuItem, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import rsiBacktestVerificationService from "@/services/rsiBacktestVerificationService";
import type { RsiBacktestTradeDetail, RsiVerificationStatus } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { inr } from "../rsi-trade-review/tradeReviewChartData";
import { fmt2, istText, signed } from "@/utils/istTime";
import { STATUSES } from "./verificationConstants";

const SRC: Record<string, { label: string; color: "default" | "primary" | "secondary" | "warning" | "error" | "info" | "success" }> = {
  STORED: { label: "Stored", color: "primary" },
  RECALC: { label: "Recalculated", color: "secondary" },
  EST: { label: "Estimated", color: "warning" },
  MISSING: { label: "Missing", color: "error" },
};
const Src = ({ k }: { k: keyof typeof SRC }) => <Chip size="small" variant="outlined" color={SRC[k].color} label={SRC[k].label} sx={{ height: 20 }} />;
const v = (x: number | null | undefined) => (x === null || x === undefined ? <Typography component="span" variant="body2" color="error.main">missing</Typography> : fmt2(x));
const num = (x: number | null | undefined) => fmt2(x);
const rText = (x: number | null | undefined) => (x === null || x === undefined ? "—" : `${signed(fmt2(x))}R`);


export default function TradeVerificationPanel({ backtestId, detail, onSaved }: { backtestId: string; detail: RsiBacktestTradeDetail; onSaved: () => void }) {
  const t = detail.trade;
  const ver = detail.verification;
  const [status, setStatus] = useState<RsiVerificationStatus | "">(t.verification_status === "UNVERIFIED" ? "" : t.verification_status);
  const [note, setNote] = useState(t.verification_note ?? "");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const ind = ver.indicators;
  const save = async () => {
    if (!status) return;
    setSaving(true);
    setMsg(null);
    try {
      await rsiBacktestVerificationService.saveVerification(backtestId, t.trade_no, status, note.trim() || null);
      setMsg({ ok: true, text: `Saved ${status} for trade #${t.trade_no}. The backtest result is unchanged.` });
      onSaved();
    } catch (e) {
      setMsg({ ok: false, text: extractErrorMessage(e) });
    } finally {
      setSaving(false);
    }
  };
  const indRows: [string, keyof typeof ind.recalculated_signal_bar][] = [
    ["RSI(9)", "rsi"], ["EMA(3) of RSI", "rsi_ema"], ["WMA(21) of RSI", "rsi_wma"], ["EMA(20)", "ema"], ["ATR(14)", "atr"], ["VWAP", "vwap"],
  ];
  const candle = (label: string, c: (typeof ver.candles)["entry"]) => (
    <TableRow key={label}>
      <TableCell>{label}</TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{c ? istText(c.time_ist) : <Src k="MISSING" />}</TableCell>
      <TableCell>{c ? num(c.open) : ""}</TableCell><TableCell>{c ? num(c.high) : ""}</TableCell>
      <TableCell>{c ? num(c.low) : ""}</TableCell><TableCell>{c ? num(c.close) : ""}</TableCell>
      <TableCell title={c?.volume_note ?? undefined}>{c ? (c.volume ? c.volume.toLocaleString("en-IN") : "N/A") : ""}</TableCell>
      <TableCell>{c && !c.valid ? <Chip size="small" color="warning" label={c.issues.join(", ")} /> : ""}</TableCell>
    </TableRow>
  );
  return (
    <Card variant="outlined" data-testid="verification-panel">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>Trade #{t.trade_no} · {t.trade_date} · {t.direction}</Typography>
          <Chip size="small" label={`Rule check: ${ver.auto_check}`} data-testid="auto-check"
            color={ver.auto_check === "PASS" ? "success" : ver.auto_check === "FAIL" ? "error" : "warning"} />
          <Chip size="small" variant="outlined" label={`Data: ${ver.data_source}`} />
          <Box sx={{ flex: 1 }} />
          <Src k="STORED" /><Src k="RECALC" /><Src k="EST" /><Src k="MISSING" />
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Why the algo entered <Src k="RECALC" /></Typography>
            {ver.entry_reason.conditions.length === 0 ? (
              <Typography variant="body2" color="warning.main">Conditions could not be re-evaluated (missing candle/indicator).</Typography>
            ) : (
              ver.entry_reason.conditions.map((c) => (
                <Typography key={c.name} variant="body2" sx={{ color: c.passed ? "success.main" : "error.main" }}>{c.passed ? "✓" : "✗"} {c.name.replace(/_/g, " ").toLowerCase()}</Typography>
              ))
            )}
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Strategy rule checks</Typography>
            <Table size="small" data-testid="rule-checks">
              <TableBody>
                {ver.rule_checks.map((c) => (
                  <TableRow key={c.rule}>
                    <TableCell sx={{ width: 28, color: c.passed === true ? "success.main" : c.passed === false ? "error.main" : "warning.main" }}>
                      {c.passed === true ? "✓" : c.passed === false ? "✗" : "?"}
                    </TableCell>
                    <TableCell>{c.rule}</TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>{c.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Trade <Src k="STORED" /></Typography>
            <Table size="small">
              <TableBody>
                {([
                  ["Entry", `${istText(t.entry_time_ist)} · ${num(t.entry_price)}`],
                  ["Stop-loss", `${num(t.stop_loss)}${t.final_stop !== null && t.final_stop !== t.stop_loss ? ` → ${num(t.final_stop)} (${t.stop_kind.toLowerCase()})` : ""}`],
                  ["Target", `${num(t.target)}${t.first_target !== null ? ` (first ${num(t.first_target)})` : ""}`],
                  ["Exit", t.exit_time_ist ? `${istText(t.exit_time_ist)} · ${num(t.exit_price)} · ${t.exit_reason}` : "— (incomplete)"],
                  ["NIFTY spot at entry / exit", `${num(ver.spot.entry)} / ${num(ver.spot.exit)} (candle open / exit level before slippage)`],
                  ["Gross / costs / net", `${signed(inr(t.gross_pnl))} − ${inr(t.costs)} = ${signed(inr(t.net_pnl))}`],
                  ["Initial risk", t.initial_risk_points == null ? "—" : `${num(t.initial_risk_points)} pts · ${inr(t.initial_risk_amount ?? null)}`],
                  ["R-multiple (total)", t.partial_qty
                    ? `${rText(t.r_multiple)} = partial ${rText(t.partial_r_multiple)} + final exit ${rText(t.final_exit_r_multiple)}`
                    : rText(t.r_multiple)],
                  ["R-multiple (net of costs)", rText(t.net_r_multiple)],
                  ["MFE / MAE", `${num(t.mfe_points)} / ${num(t.mae_points)} pts`],
                  ["Holding", t.holding_minutes === null ? "—" : `${num(t.holding_minutes)} min`],
                  ["Partial booking", t.partial_qty ? `${t.partial_qty} units at ${num(t.partial_price)} (${istText(t.partial_time_ist)}), ${signed(inr(t.partial_gross_pnl))}` : "none"],
                  ["Size", `${t.lots ?? "—"} lots · ${t.quantity ?? "—"} units`],
                ] as [string, string][]).map(([k, val]) => (
                  <TableRow key={k}><TableCell sx={{ color: "text.secondary", width: "38%" }}>{k}</TableCell><TableCell>{val}</TableCell></TableRow>
                ))}
                <TableRow>
                  <TableCell sx={{ color: "text.secondary" }}>Option <Src k="EST" /></TableCell>
                  <TableCell>
                    {ver.option.contract ?? "No contract simulated"} · delta {num(ver.option.delta_used)} · premium {inr(ver.option.premium_assumed_for_costs)} (costs only) · {ver.option.status}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Typography variant="subtitle2">Candles (IST)</Typography>
            <Box sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="verification-candles">
              <TableHead><TableRow>{["", "Time", "O", "H", "L", "C", "Vol", ""].map((h, i) => <TableCell key={i}>{h}</TableCell>)}</TableRow></TableHead>
              <TableBody>
                {candle("Signal", ver.candles.signal)}{candle("Entry", ver.candles.entry)}
                {ver.partial ? candle("Partial", ver.partial.candle) : null}{candle("Exit", ver.candles.exit)}
              </TableBody>
            </Table>
            </Box>
          </Grid>
          <Grid item xs={12} lg={5}>
            <Typography variant="subtitle2">Indicators</Typography>
            <Box sx={{ overflowX: "auto" }}>
            <Table size="small" data-testid="verification-indicators">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Signal bar <Src k="STORED" /></TableCell>
                  <TableCell>Signal bar <Src k="RECALC" /></TableCell>
                  <TableCell>Entry <Src k="RECALC" /></TableCell>
                  <TableCell>Exit <Src k="RECALC" /></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {indRows.map(([label, k]) => (
                  <TableRow key={k}>
                    <TableCell>{label}</TableCell>
                    <TableCell>{v(ind.stored_signal_bar[k])}</TableCell>
                    <TableCell>{v(ind.recalculated_signal_bar[k])}</TableCell>
                    <TableCell>{v(ind.recalculated_entry_bar[k])}</TableCell>
                    <TableCell>{v(ind.recalculated_exit_bar[k])}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </Box>
          </Grid>
        </Grid>

        {ver.warnings.length > 0 ? (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="verification-warnings">
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>Data-quality warnings{ver.data_quality_flags?.length ? ` (${ver.data_quality_flags.join(", ")})` : ""}</Typography>
            {ver.warnings.map((w) => <Typography key={w} variant="caption" sx={{ display: "block" }}>{w}</Typography>)}
          </Alert>
        ) : (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="verification-no-data-warnings">No data-quality problems found for this trade.</Alert>
        )}
        {(ver.model_limitations ?? []).length > 0 && (
          <Alert severity="info" sx={{ mt: 1 }} data-testid="model-limitations">
            <Typography variant="caption" sx={{ display: "block", fontWeight: 700 }}>Model limitation (not a data problem)</Typography>
            {(ver.model_limitations ?? []).map((m) => <Typography key={m.code} variant="caption" sx={{ display: "block" }}>{m.code}: {m.message}</Typography>)}
          </Alert>
        )}

        <Box sx={{ mt: 2, p: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
          <Typography variant="subtitle2">Manual verification <Typography component="span" variant="caption" color="text.secondary">— annotation only; never changes the backtest, P&L or trading</Typography></Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }} alignItems={{ sm: "flex-start" }}>
            <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value as RsiVerificationStatus)} sx={{ minWidth: 220 }}>
              {STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline maxRows={4} inputProps={{ maxLength: 2000 }} />
            <Button variant="contained" disabled={!status || saving} onClick={() => void save()}>{saving ? "Saving…" : "Save"}</Button>
          </Stack>
          {t.verification_updated_at && (
            <Typography variant="caption" color="text.secondary" data-testid="verification-saved-at">Last saved {t.verification_updated_at_ist ?? istText(t.verification_updated_at)} by {t.verification_updated_by}</Typography>
          )}
          {msg && <Alert severity={msg.ok ? "success" : "error"} sx={{ mt: 1 }} data-testid="verification-msg">{msg.text}</Alert>}
        </Box>
      </CardContent>
    </Card>
  );
}
